/**
 * Odino UI context (ADR-0024 slice A).
 *
 * Derives "where is the operator right now" from the current route so the rail
 * can send it with each turn — then Odino resolves "this app / this page" without
 * asking for the slug. Pure + unit-tested; the backend renders only known keys
 * (`hub.odino.orchestrator.render_ui_context`), so this stays a thin route parser.
 *
 * Keep the `area` strings in sync with the backend's `_AREA_LABELS`.
 */
export interface OdinoUiContext {
  /** App UUID from the route (get_artifact / apply_patch accept it directly). */
  app_id?: string;
  /** Coarse location label, mirrored in the backend's _AREA_LABELS. */
  area: string;
  page_id?: string;
  table_id?: string;
  form_id?: string;
  dataset_id?: string;
  /** Workflow UUID when the operator is in the workflow editor. */
  workflow_id?: string;
  /** ETL pipeline UUID when the operator is in the ETL designer. */
  pipeline_id?: string;
}

const UUID_RE = /^[0-9a-fA-F-]{36}$/;

/**
 * Map a pathname to a context, or null when the operator isn't in a
 * Odino-relevant area (so the rail sends no context and Odino doesn't assume).
 * Covers the apps area PLUS the flow editors — "in quale workflow siamo?"
 * must resolve without the operator naming it (draft-first live-test fix).
 */
export function deriveOdinoContext(pathname: string): OdinoUiContext | null {
  // Flow editors — /workflows/:id (and the legacy /automations alias) + /etl/:id.
  const wf = pathname.match(/^\/(?:workflows|automations)\/([^/]+)/);
  if (wf && UUID_RE.test(wf[1])) {
    return { workflow_id: wf[1], area: "workflow_editor" };
  }
  const etl = pathname.match(/^\/etl\/([^/]+)/);
  if (etl && etl[1] !== "lanes" && UUID_RE.test(etl[1])) {
    return { pipeline_id: etl[1], area: "etl_designer" };
  }

  // Exact apps list.
  if (pathname === "/apps" || pathname === "/apps/") {
    return { area: "apps_list" };
  }
  const m = pathname.match(/^\/apps\/([^/]+)(?:\/(.*))?$/);
  if (!m) return null;
  const app_id = m[1];
  const rest = m[2] ?? "";

  if (rest === "" ) return { app_id, area: "app_detail" };
  if (rest === "access") return { app_id, area: "app_access" };

  const sub = rest.match(/^(pages|tables|forms|datasets)\/([^/]+)/);
  if (sub) {
    const [, kind, id] = sub;
    if (kind === "pages") return { app_id, area: "page_builder", page_id: id };
    if (kind === "tables") return { app_id, area: "table_designer", table_id: id };
    if (kind === "forms") return { app_id, area: "form_builder", form_id: id };
    if (kind === "datasets") return { app_id, area: "dataset_editor", dataset_id: id };
  }
  // Some other app sub-route — still scope to the app.
  return { app_id, area: "app_detail" };
}
